import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import CapsuleTab from '@repo/ui/capsule-tab'
import { ChevronLeftIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'

import { getCenter } from '../../api/centers/get-center'
import CountryFlag from '../../components/elements/country-flag'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { CenterEventTable } from '../../components/pages/center-events/center-event-table'
import { Paths } from '../../lib/constants/path'
import { CenterProperties } from '../../lib/types/models/center/properties.center'
import NotFoundPage from '../common/not-found'
import CenterGlobalView from './center-details/center-global-view'
import CenterGroups from './center-details/center-groups'
import CenterOlInformation from './center-details/center-ol-information'
import { CenterStaffs } from './center-details/center-staffs'
import CenterStudents from './center-details/center-students'
import { CenterTrips } from './center-details/center-trips'

const pathsMapping: Record<number, string> = {
  0: Paths.CENTER_GLOBAL_VIEW_PAGE,
  1: Paths.CENTER_GROUPS_PAGE,
  2: Paths.CENTER_YOUNG_PEOPLE_PAGE,
  3: Paths.CENTER_STAFFS_PAGE,
  4: Paths.CENTER_TRIPS_PAGE,
  5: Paths.CENTER_FOREIGN_PROVIDER_PAGE,
  6: Paths.CENTER_STAY_EVENTS_PAGE,
  7: Paths.CENTER_STATISTICS_PAGE
}

const CenterUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState(0)
  const { pathname } = useLocation()
  const { navigate, back } = useNavigator()

  if (!id || isNaN(parseInt(id)))
    return <ItemNotFound title="Le centre que vous cherchez n'existe pas" />

  const { data: center, isPending } = useQuery({
    queryKey: ['centers', id],
    queryFn: () => getCenter(parseInt(id))
  })

  useEffect(() => {
    for (const [index, path] of Object.entries(pathsMapping)) {
      if (pathname.includes(path)) {
        setActiveTab(+index)
        return
      }
    }
  }, [pathname])

  if (isPending) return <LoadingScreen />
  if (!center) return <NotFoundPage />

  return (
    <div>
      <UserNavBar
        icon={ChevronLeftIcon}
        title={center.name}
        onIconClick={() => back(Paths.CENTER_LIST_PAGE)}
      />
      <div className={'h-4'} />
      <Box>
        <div className="flex items-center gap-2">
          <Typography variant="h4" color={tailwindColors.black.DEFAULT}>
            {CenterProperties.getCenterFormulaName(center).join(', ')}
          </Typography>
          <CountryFlag
            size="large"
            possibleCountry={center.journeyPlace?.country.name}
            code={center.journeyPlace?.country.code}
          />
          <Badge variant="grey" size="small" text={CenterProperties.getCenterDates(center)} />
        </div>
        <div className="h-5" />
        <CapsuleTab
          variant="secondary"
          activeTab={activeTab}
          onTabChange={(index) => {
            // If the user already navigated to the tab, we don't want to navigate again
            if (pathname.includes(pathsMapping[index])) return
            navigate(pathsMapping[index])
          }}
          tabs={[
            { label: 'Vue globale' },
            { label: 'Sous-groupes' },
            { label: `Jeunes` },
            { label: 'Encadrants' },
            { label: 'Voyage' },
            { label: 'Prestataire étranger' },
            { label: 'Evènements en séjour' }
            // { label: 'Statistiques' }
          ]}
        />

        <Routes>
          <Route
            path={Paths.CENTER_GLOBAL_VIEW_PAGE}
            element={<CenterGlobalView id={center.id} />}
          />
          <Route
            path={Paths.CENTER_GROUPS_PAGE}
            element={
              <CenterGroups
                id={center.id}
                mode="view"
                permissions={['create', 'update', 'delete', 'upload-documents', 'view-students']}
              />
            }
          />
          <Route path={Paths.CENTER_YOUNG_PEOPLE_PAGE} element={<CenterStudents />} />
          <Route path={Paths.CENTER_TRIPS_PAGE} element={<CenterTrips centerId={center.id} />} />
          <Route path={Paths.CENTER_STAFFS_PAGE} element={<CenterStaffs id={center.id} />} />
          <Route
            path={Paths.CENTER_FOREIGN_PROVIDER_PAGE}
            element={<CenterOlInformation id={center.id} />}
          />
          <Route
            path={Paths.CENTER_STAY_EVENTS_PAGE}
            element={
              <InnerBox className="m-5 rounded-2xl p-0">
                <CenterEventTable centerId={center.id} version={'PARTIAL'} />
              </InnerBox>
            }
          />
          <Route path={Paths.CENTER_STATISTICS_PAGE} element={<p>Statistics</p>} />
          <Route path={'/'} element={<Navigate to={Paths.CENTER_GLOBAL_VIEW_PAGE} />} />
          <Route path={'*'} element={<NotFoundPage />} />
        </Routes>
      </Box>
    </div>
  )
}

export default CenterUpdatePage
